import * as React from 'react';
import { useEffect, useState, Fragment, useRef } from 'react';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Material UI Packages. */
import { Box, Dialog, DialogContent, Slide } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import DialogHeader from 'shared/ui/DialogHeader';

import validateFields from 'shared/lib/validateFields';
import ItemDetailForm from './ItemDetailForm';
import { MESSAGES, showSnackbar, isDuplicateName } from '../../../lib/configUtils';

const initValidator = {
    il_trade_partner: false,
    il_duty_item_category: false,
    item_name: false,
    item_code: false,
    id: false,
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function ItemDetail(props) {
    const {
        newOpen,
        setNewOpen,
        selectedRowId,
        itemRows,
        setItemRows,
        loadingState,
        itemFormulasRows,
        setLoadingState,
    } = props;

    const dispatch = useDispatch();

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** State for savintg all inputs in the item detail dialog. */
    const [itemDetail, setItemDetail] = useState();
    /** State for validating and displaying errors for required inputs. */
    const [validator, setValidator] = useState(initValidator);

    const initialItemDetailRef = useRef(null);

    const hasItemDetailChanged = () => {
        if (!itemDetail || !initialItemDetailRef.current) return false;
        return JSON.stringify(itemDetail) !== JSON.stringify(initialItemDetailRef.current);
    };

    /** Handler for closing dialog and detecting changes. */
    const handleClose = (options = {}) => {
        const { skipUnsavedCheck = false } = options;
        if (skipUnsavedCheck) {
            initialItemDetailRef.current = itemDetail;
            setNewOpen(false);
            return;
        }
        if (hasItemDetailChanged()) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (!confirmed) {
                return;
            }
        }
        setItemDetail(initialItemDetailRef.current ?? undefined);
        setNewOpen(false);
    };

    /** Function to validate required fields. */
    const isInputVerified = () => {
        if (!itemDetail) {
            return false;
        }

        const validationRules = {
            il_trade_partner: itemDetail.il_trade_partner,
            il_duty_item_category: itemDetail.il_duty_item_category,
            item_name: itemDetail.item_name,
            item_code: itemDetail.item_code,
            id: itemDetail.id,
        };

        return validateFields(validationRules, setValidator);
    };

    const handleSaveBtn = () => {
        if (!isInputVerified()) {
            dispatch(setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'warning' }));
            return;
        }

        const otherItems = itemRows.filter((item) => item.id !== itemDetail.id);
        if (isDuplicateName(otherItems, itemDetail.item_code, 'item_code')) {
            showSnackbar(dispatch, MESSAGES.DUPLICATE_ITEMS, 'warning');
            return;
        }

        callAPI({
            method: 'put',
            module: 'duty_calculator',
            component: 'duty_item',
            getAccessTokenSilently,
            email: user.email,
            obj: itemDetail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onSuccess: (resp) => {
                console.log('Item detail response:', resp);
                setItemRows((prev) => prev.map((item) => (item.id === itemDetail.id ? resp.data : item)));
                dispatch(setSnackbar({ open: true, message: 'Successfully Saved.', severity: 'success' }));
                handleClose({ skipUnsavedCheck: true });
            },
            // 서버가 거절한 이유(Tier 무관 품목에 Tier별 수식 둘 이상 등)를 그대로 보여 준다
            onError: (error) =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: error?.message || 'Something went wrong. Please try again.',
                        severity: 'error',
                    }),
                ),
            onFinal: () => setLoadingState((prev) => ({ ...prev, circularLoading: false })),
        });
    };

    useEffect(() => {
        if (selectedRowId) {
            const row = itemRows.find((item) => item.id === selectedRowId);
            setItemDetail(row);
            initialItemDetailRef.current = row ? { ...row } : null;
        }
    }, [selectedRowId, itemRows]);

    return (
        <Fragment>
            <Dialog fullScreen open={newOpen} onClose={handleClose} TransitionComponent={Transition}>
                <DialogHeader
                    title={'EDIT ITEM DETAIL'}
                    onClose={handleClose}
                    rightChildren={
                        <LoadingButton
                            onClick={handleSaveBtn}
                            sx={{
                                color: '#00647e',
                                bgcolor: 'white',
                                width: '52px',
                                height: '30px',
                                borderRadius: '3px',
                                ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                            }}
                        >
                            SAVE
                        </LoadingButton>
                    }
                />
                <DialogContent sx={{ py: 0 }}>
                    <Box sx={{ px: 2, py: 4 }}>
                        <ItemDetailForm
                            itemDetail={itemDetail}
                            setItemDetail={setItemDetail}
                            loadingState={loadingState}
                            itemFormulasRows={itemFormulasRows}
                            validator={validator}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}
